// =============================================================================
// SIREPPAR - Formulario de Nuevo Expediente
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { expedienteService, partidoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FileUpload from '../../components/common/FileUpload';

// =============================================================================
// Tipos y Constantes
// =============================================================================

const tiposExpediente = [
  { value: 'RECONOCIMIENTO', label: 'Reconocimiento de Personería', descripcion: 'Solicitud de reconocimiento jurídico-político', dias: 90 },
  { value: 'REEMPADRONAMIENTO', label: 'Reempadronamiento', descripcion: 'Actualización del padrón de afiliados', dias: 60 },
  { value: 'CAMBIO_AUTORIDADES', label: 'Cambio de Autoridades', descripcion: 'Comunicación de nuevas autoridades partidarias', dias: 30 },
  { value: 'CAMBIO_DENOMINACION', label: 'Cambio de Denominación', descripcion: 'Modificación del nombre del partido', dias: 45 },
  { value: 'FUSION', label: 'Fusión', descripcion: 'Fusión con otro partido político', dias: 90 },
  { value: 'CONFEDERACION', label: 'Confederación', descripcion: 'Conformación de confederación', dias: 60 },
  { value: 'SUBSANACION', label: 'Subsanación', descripcion: 'Subsanación de observaciones', dias: 15 },
  { value: 'CADUCIDAD', label: 'Caducidad', descripcion: 'Trámite de caducidad', dias: 30 },
  { value: 'EXTINCION', label: 'Extinción', descripcion: 'Trámite de extinción', dias: 30 },
];

const prioridades = [
  { value: 'BAJA', label: 'Baja', color: 'bg-gray-100 text-gray-700' },
  { value: 'MEDIA', label: 'Media', color: 'bg-blue-100 text-blue-700' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  { value: 'URGENTE', label: 'Urgente', color: 'bg-red-100 text-red-700' },
];

// =============================================================================
// Esquema de Validación
// =============================================================================

const expedienteSchema = z.object({
  tipo: z.string().min(1, 'Seleccione un tipo de trámite'),
  partidoId: z.string().uuid('Seleccione un partido'),
  asunto: z.string().min(10, 'El asunto debe tener al menos 10 caracteres').max(500),
  descripcion: z.string().max(2000).optional(),
  prioridad: z.enum(['BAJA', 'MEDIA', 'ALTA', 'URGENTE']),
});

type ExpedienteFormData = z.infer<typeof expedienteSchema>;

// =============================================================================
// Componente
// =============================================================================

export default function ExpedienteFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const partidoIdParam = searchParams.get('partidoId');
  const isExterno = ['APODERADO_FIRMANTE', 'APODERADO_GESTOR'].includes(user?.rol || '');

  const [currentStep, setCurrentStep] = useState(0);
  const [archivosAdjuntos, setArchivosAdjuntos] = useState<File[]>([]);

  // Form
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ExpedienteFormData>({
    resolver: zodResolver(expedienteSchema),
    defaultValues: {
      tipo: '',
      partidoId: partidoIdParam || (isExterno ? user?.partidoId : '') || '',
      asunto: '',
      descripcion: '',
      prioridad: 'MEDIA',
    },
  });

  const tipoSeleccionado = watch('tipo');
  const partidoSeleccionado = watch('partidoId');

  // Query partidos (solo para usuarios internos)
  const { data: partidos } = useQuery({
    queryKey: ['partidos-select'],
    queryFn: async () => {
      const response = await partidoService.list({ pageSize: 100, estado: 'RECONOCIDO' });
      return response.data.data;
    },
    enabled: !isExterno,
  });

  // Query partido seleccionado
  const { data: partidoInfo } = useQuery({
    queryKey: ['partido', partidoSeleccionado],
    queryFn: async () => {
      const response = await partidoService.getById(partidoSeleccionado);
      return response.data.data;
    },
    enabled: !!partidoSeleccionado,
  });

  // Mutation crear expediente
  const createMutation = useMutation({
    mutationFn: async (data: ExpedienteFormData) => {
      return expedienteService.create(data);
    },
    onSuccess: (response) => {
      toast.success('Expediente creado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['expedientes'] });
      navigate(`/expedientes/${response.data.data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear expediente');
    },
  });

  const onSubmit = (data: ExpedienteFormData) => {
    createMutation.mutate(data);
  };

  // Steps
  const steps = [
    { id: 'tipo', title: 'Tipo de Trámite', description: 'Seleccione el tipo de expediente' },
    { id: 'datos', title: 'Datos del Expediente', description: 'Complete la información' },
    { id: 'documentos', title: 'Documentos', description: 'Adjunte documentación' },
    { id: 'revision', title: 'Revisión', description: 'Confirme y envíe' },
  ];

  const tipoInfo = tiposExpediente.find(t => t.value === tipoSeleccionado);

  const canProceed = () => {
    if (currentStep === 0) return !!tipoSeleccionado;
    if (currentStep === 1) return !!partidoSeleccionado && watch('asunto')?.length >= 10;
    return true;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link to="/expedientes" className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1 mb-2">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al listado
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Nuevo Expediente</h1>
        <p className="text-gray-500 mt-1">Complete los datos para iniciar un nuevo trámite</p>
      </div>

      {/* Progress Steps */}
      <nav className="mb-8">
        <ol className="flex items-center">
          {steps.map((step, index) => (
            <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
              {index !== steps.length - 1 && (
                <div className="absolute top-4 left-7 -right-3 sm:left-9 sm:-right-11">
                  <div className={`h-0.5 w-full ${index < currentStep ? 'bg-primary-600' : 'bg-gray-200'}`} />
                </div>
              )}
              <button
                type="button"
                onClick={() => index < currentStep && setCurrentStep(index)}
                disabled={index > currentStep}
                className="relative flex items-start group"
              >
                <span className="flex h-9 items-center">
                  <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                    index < currentStep
                      ? 'bg-primary-600 text-white'
                      : index === currentStep
                      ? 'border-2 border-primary-600 bg-white text-primary-600'
                      : 'border-2 border-gray-300 bg-white text-gray-500'
                  }`}>
                    {index < currentStep ? '✓' : index + 1}
                  </span>
                </span>
                <span className="ml-4 min-w-0 flex-col hidden sm:flex">
                  <span className={`text-sm font-medium ${index <= currentStep ? 'text-primary-600' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white rounded-lg shadow">
          
          {/* Step 1: Tipo de Trámite */}
          {currentStep === 0 && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">Seleccione el tipo de trámite</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tiposExpediente.map((tipo) => (
                  <label
                    key={tipo.value}
                    className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none transition-all ${
                      tipoSeleccionado === tipo.value
                        ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      {...register('tipo')}
                      value={tipo.value}
                      className="sr-only"
                    />
                    <div className="flex flex-1">
                      <div className="flex flex-col">
                        <span className={`block text-sm font-medium ${
                          tipoSeleccionado === tipo.value ? 'text-primary-900' : 'text-gray-900'
                        }`}>
                          {tipo.label}
                        </span>
                        <span className={`mt-1 text-sm ${
                          tipoSeleccionado === tipo.value ? 'text-primary-700' : 'text-gray-500'
                        }`}>
                          {tipo.descripcion}
                        </span>
                        <span className="mt-2 text-xs text-gray-400">
                          Plazo estimado: {tipo.dias} días
                        </span>
                      </div>
                    </div>
                    {tipoSeleccionado === tipo.value && (
                      <div className="shrink-0 text-primary-600">
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {errors.tipo && <p className="text-red-500 text-sm mt-2">{errors.tipo.message}</p>}
            </div>
          )}

          {/* Step 2: Datos del Expediente */}
          {currentStep === 1 && (
            <div className="p-6 space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Datos del Expediente</h2>
              
              {/* Info del tipo seleccionado */}
              {tipoInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Tipo:</strong> {tipoInfo.label} • <strong>Plazo:</strong> {tipoInfo.dias} días
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Partido */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Partido Político *
                  </label>
                  {isExterno ? (
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <p className="font-medium">{partidoInfo?.denominacion || 'Cargando...'}</p>
                      <p className="text-sm text-gray-500">{partidoInfo?.sigla}</p>
                    </div>
                  ) : (
                    <select
                      {...register('partidoId')}
                      className={`w-full border rounded-lg px-3 py-2 ${errors.partidoId ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Seleccione un partido</option>
                      {partidos?.map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.numero} - {p.sigla} - {p.denominacion}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.partidoId && <p className="text-red-500 text-sm mt-1">{errors.partidoId.message}</p>}
                </div>

                {/* Asunto */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asunto *
                  </label>
                  <input
                    type="text"
                    {...register('asunto')}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.asunto ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Breve descripción del trámite"
                  />
                  {errors.asunto && <p className="text-red-500 text-sm mt-1">{errors.asunto.message}</p>}
                </div>

                {/* Descripción */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    {...register('descripcion')}
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Detalle adicional del trámite (opcional)"
                  />
                </div>

                {/* Prioridad (solo internos) */}
                {!isExterno && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prioridad
                    </label>
                    <select
                      {...register('prioridad')}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      {prioridades.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Documentos */}
          {currentStep === 2 && (
            <div className="p-6 space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Documentación</h2>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Los documentos se pueden adjuntar ahora o posteriormente desde el detalle del expediente.
                </p>
              </div>

              <FileUpload
                onUpload={async (files: File[]) => setArchivosAdjuntos(files)}
                maxFiles={10}
                maxSize={25}
                multiple={true}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />

              {archivosAdjuntos.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Archivos a adjuntar:</h3>
                  <ul className="space-y-2">
                    {archivosAdjuntos.map((file, index) => (
                      <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setArchivosAdjuntos(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-500 text-sm"
                        >
                          Quitar
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Revisión */}
          {currentStep === 3 && (
            <div className="p-6 space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Revisión Final</h2>
              
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Tipo de Trámite</span>
                    <p className="font-medium">{tipoInfo?.label}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Plazo Estimado</span>
                    <p className="font-medium">{tipoInfo?.dias} días</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">Partido</span>
                    <p className="font-medium">{partidoInfo?.denominacion}</p>
                    <p className="text-sm text-gray-500">{partidoInfo?.sigla}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">Asunto</span>
                    <p className="font-medium">{watch('asunto')}</p>
                  </div>
                  {watch('descripcion') && (
                    <div className="col-span-2">
                      <span className="text-sm text-gray-500">Descripción</span>
                      <p className="text-gray-700">{watch('descripcion')}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-gray-500">Prioridad</span>
                    <p className="font-medium">{prioridades.find(p => p.value === watch('prioridad'))?.label}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Documentos adjuntos</span>
                    <p className="font-medium">{archivosAdjuntos.length} archivo(s)</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Al crear el expediente, se generará un número único y quedará en estado <strong>Borrador</strong> 
                  hasta que lo presente formalmente.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={() => navigate('/expedientes')}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
          >
            Cancelar
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Anterior
              </button>
            )}

            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            ) : (
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creando...' : 'Crear Expediente'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
